/** @format */
import { css } from 'styled-components';

import { AccordionLayout, AccordionProps } from './index';

/**
 * Base styles for the accordion component
 *
 * @param {AccordionProps} props The accordion properties object
 * */
const baseStyles = (props: AccordionProps) => css`
    .panel {
        border-bottom: 0.1rem solid var(--colors-border-tertiary);

        &:last-of-type {
            border-bottom: none;
        }
    }

    .panel-inner {
        visibility: hidden;

        /* Makes the element include margins of direct children in the height of it */
        &::before,
        &::after {
            content: '';
            display: table;
        }
    }

    .title {
        margin: 0;
        display: grid;
        grid-template-columns: auto auto auto;
        grid-template-rows: auto auto;
        gap: 0px 0px;
        grid-template-areas:
            'button button tick'
            'summary extra extra';
    }

    .title-button {
        transition: color 0.3s ease-in-out;
        background: transparent;
        border: none;
        display: block;
        text-align: left;
        padding: 1.8rem 0;
        font-weight: inherit;
        grid-area: button;
    }

    .title-summary {
        font-size: 1.2rem;
        font-weight: 300;
        grid-area: summary;

        * {
            margin: 0;
        }
    }

    .title-extra {
        margin-bottom: var(--layout-spacing-24);
        align-items: center;
        color: var(--colors-text-primary);
        transition: color 0.3s ease-in-out;
    }

    .title-extra-node-wrapper {
        grid-area: extra;
    }

    .title-extra-node {
        margin-left: 1rem;
        justify-content: end;

        /*
         * The summary and extra node don't align unless
         * this is set to display: flex
         */
        display: flex;
    }

    .tick-wrapper {
        display: flex;
        align-items: center;
        justify-content: end;
        grid-area: tick;
    }

    .title-action {
        justify-self: end;
        align-self: baseline;
        margin-top: 1.4rem;
    }

    .tick {
        width: 2.4rem;
        margin-left: 1rem;

        svg {
            display: block;
            fill: var(--colors-text-tertiary);
        }
    }
`;

/**
 * Layout styles for the accordion component
 *
 * @param {AccordionProps} props The accordion properties object
 * @param {AccordionLayout} props.layout The accordion layout
 * */
const layoutStyles = ({ layout }: AccordionProps) => {
    switch (layout) {
        case AccordionLayout.CHECKOUT:
            return css`
                .panel-content {
                    padding: 0.6rem 0 3rem;
                }

                .title-button {
                    color: var(--colors-text-primary);

                    &--less-padding {
                        padding-bottom: 1.2rem;
                    }

                    &[aria-expanded='true'] {
                        color: var(--colors-text-secondary);

                        ~ .title-extra {
                            color: var(--colors-text-secondary);
                        }
                    }
                }
            `;

        case AccordionLayout.ARROW:
        default:
            return css`
                .title {
                    padding: 0 var(--layout-spacing-12);
                }

                .title-button {
                    display: flex;
                    align-items: center;
                    color: var(--colors-text-secondary);
                    &[aria-expanded='true'] {
                        .title-arrow {
                            transform: rotate(90deg);
                        }
                    }
                }

                .panel-content {
                    padding: 0.6rem var(--layout-spacing-24) 3rem;
                }

                .title-arrow {
                    font-size: 1.4rem;
                    margin-right: 1rem;
                    transition: transform 0.3s ease-in-out;
                }
            `;
    }
};

/**
 * The accordion component styles
 *
 * @param {AccordionProps} props The accordion properties object
 * */
export const styles = (props: AccordionProps) => css`
    ${baseStyles(props)}
    ${layoutStyles(props)}
`;
